import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API, getAuthHeaders } from '../App';
import { useSocket } from '../contexts/SocketContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Smartphone, Check, Loader2, QrCode, Wifi, WifiOff } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MobilePairingDialog = ({ open, onOpenChange }) => {
  const { isConnected, mobileConnected, setMobileConnected, connect, disconnect } = useSocket();
  const [loading, setLoading] = useState(false);
  const [qrCode, setQrCode] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [pairingStatus, setPairingStatus] = useState('idle'); // idle, generating, waiting, paired
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);

  useEffect(() => {
    if (open && !mobileConnected) {
      generatePairingCode();
    }
    
    return () => {
      // Cleanup WebSocket when dialog closes
      if (!open && wsRef.current) {
        // Don't close if successfully paired
        if (pairingStatus !== 'paired') {
          // Keep connection alive
        }
      }
    };
  }, [open]);

  const connectWebSocket = (session_id, user_id) => {
    // Convert HTTP to WS
    const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const fullUrl = `${wsUrl}/api/ws/desktop/${session_id}?user_id=${user_id}`;
    
    console.log('Connecting desktop WebSocket:', fullUrl);
    
    const ws = new WebSocket(fullUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('✅ Desktop WebSocket connected');
      setWsConnected(true);
      setPairingStatus('waiting');
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('📩 Desktop received:', data);
        
        if (data.type === 'mobile_paired') {
          console.log('📱 Mobile paired successfully!');
          setPairingStatus('paired');
          setMobileConnected(true);
          toast.success('Mobile device paired successfully! 📱');
          
          setTimeout(() => {
            onOpenChange(false);
          }, 2000);
        }
      } catch (e) {
        console.error('Failed to parse message:', e);
      }
    };

    ws.onclose = () => {
      console.log('❌ Desktop WebSocket disconnected');
      setWsConnected(false);
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    return ws;
  };

  const generatePairingCode = async () => {
    setLoading(true);
    setPairingStatus('generating');

    try {
      const response = await axios.post(`${API}/mobile/generate-pairing`, {}, {
        headers: getAuthHeaders()
      });

      const { session_id, qr_code, user_id } = response.data;
      
      setQrCode(qr_code);
      setSessionId(session_id);

      // Connect WebSocket for this session
      connectWebSocket(session_id, user_id);

      toast.success('QR Code generated! Scan with your Android device');
    } catch (error) {
      console.error('Error generating pairing code:', error);
      toast.error('Failed to generate pairing code');
      setPairingStatus('idle');
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    // Close existing WebSocket
    if (wsRef.current) {
      wsRef.current.close();
    }
    setQrCode(null);
    setSessionId(null);
    setPairingStatus('idle');
    setWsConnected(false);
    generatePairingCode();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl font-heading tracking-tight flex items-center gap-2">
            <Smartphone className="h-6 w-6 text-blue-600" />
            Pair Android Device
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Connection Status */}
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <span className="text-sm text-slate-600">Connection Status:</span>
            <div className="flex items-center gap-2">
              {wsConnected ? (
                <>
                  <Wifi className="h-4 w-4 text-green-600" />
                  <span className="text-sm font-medium text-green-600">Ready</span>
                </>
              ) : pairingStatus === 'generating' ? (
                <>
                  <Loader2 className="h-4 w-4 text-blue-600 animate-spin" />
                  <span className="text-sm font-medium text-blue-600">Connecting...</span>
                </>
              ) : (
                <>
                  <WifiOff className="h-4 w-4 text-slate-400" />
                  <span className="text-sm font-medium text-slate-400">Not connected</span>
                </>
              )}
            </div>
          </div>

          {/* Pairing Status */}
          {pairingStatus === 'idle' && (
            <div className="text-center py-8">
              <Button 
                onClick={generatePairingCode}
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="generate-qr-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <QrCode className="h-4 w-4 mr-2" />
                    Generate QR Code
                  </>
                )}
              </Button>
            </div>
          )}

          {pairingStatus === 'generating' && (
            <div className="text-center py-8">
              <Loader2 className="h-12 w-12 mx-auto text-blue-600 animate-spin mb-4" />
              <p className="text-slate-600">Generating QR code...</p>
            </div>
          )}

          {pairingStatus === 'waiting' && qrCode && (
            <div className="space-y-4">
              <div className="bg-white p-4 rounded-lg border-2 border-blue-200">
                <img 
                  src={qrCode} 
                  alt="Pairing QR Code" 
                  className="w-full h-auto"
                  data-testid="pairing-qr-code"
                />
              </div>
              
              <div className="space-y-2">
                <p className="text-center text-sm font-medium text-slate-900">
                  Scan this QR code with your Android device
                </p>
                <div className="bg-blue-50 p-4 rounded-lg space-y-2">
                  <p className="text-xs text-blue-900 font-medium">Instructions:</p>
                  <ol className="text-xs text-blue-800 space-y-1 list-decimal list-inside">
                    <li>Open <strong>{BACKEND_URL}/mobile</strong> on your phone</li>
                    <li>Tap "Scan QR Code" button</li>
                    <li>Point camera at this QR code</li>
                    <li>Wait for connection confirmation</li>
                  </ol>
                </div>
              </div>

              <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                Waiting for mobile device...
              </div>

              <Button 
                onClick={handleRetry}
                variant="outline"
                className="w-full"
              >
                Generate New Code
              </Button>
            </div>
          )}

          {pairingStatus === 'paired' && (
            <div className="text-center py-8">
              <div className="h-16 w-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                <Check className="h-8 w-8 text-green-600" />
              </div>
              <p className="text-lg font-medium text-slate-900 mb-2">Device Paired Successfully!</p>
              <p className="text-sm text-slate-600">Your Android device is now connected</p>
            </div>
          )}

          {/* Info Box */}
          <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
            <p className="text-xs text-amber-900">
              <strong>Note:</strong> Make sure your mobile device is connected to the internet. 
              The QR code expires in 5 minutes.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MobilePairingDialog;
